/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-range.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import React, { useState, useMemo, useEffect } from 'react';

import { IPanelGraphCategory } from './panelGraphCategory.interface';
import { useBoolean, useTranslation } from 'hooks';
import { PanelGraphCategoryClause } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

const EditRange = ({
    open,
    onClose,
    currentCategory,
    categories,
}: {
    open: boolean;
    onClose: Function;
    currentCategory?: IPanelGraphCategory;
    categories: IPanelGraphCategory[];
}) => {
    const initialErrors = { type: '', clause: '', min: '', max: '' };
    const initialState = { type: '', clause: '', min: '', max: '' };
    const { t } = useTranslation();
    const [errors, setErrors] = useState({ ...initialErrors } as any);
    const [state, setState] = useState({ ...initialState });
    const disabledSave = useBoolean(true);

    const types = useMemo(
        () => [
            {
                id: '',
                text: t('ra:selectValue'),
            },
            {
                id: 'VALUE',
                text: t('values'),
            },
            {
                id: 'CLAUSE',
                text: t('cm:clause'),
            },
        ],
        [],
    );

    useEffect(() => {
        if (currentCategory && open)
            setState({
                type:
                    currentCategory.type ??
                    (currentCategory.clause && currentCategory.clause !== PanelGraphCategoryClause.NONE
                        ? 'CLAUSE'
                        : 'VALUE'),
                clause: currentCategory.clause,
                min: currentCategory.min as string,
                max: currentCategory.max as string,
            });
        else if (!open) {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        }
    }, [currentCategory, open]);

    const checkIfCategoryExists = () => {
        let isExists = false;
        // this field is available in both cases
        if (state.type === 'VALUE') {
            isExists = checkIfNameExist(
                { ...state, max: `${state.max}`, index: currentCategory?.index ?? categories.length },
                categories,
                ['min', 'max'],
                'index',
            );
        } else if (state.type === 'CLAUSE') {
            isExists = checkIfNameExist(
                { ...state, clause: state.clause.trim(), index: currentCategory?.index ?? categories.length },
                categories,
                ['min', 'clause'],
                'index',
            );
        }

        return isExists;
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const validateError = validateFields(value, field, length, required);

        setErrors({
            ...errors,
            ...validateError,
        });
    };

    const onChange = (name: string, length?: number) => (event: any) => {
        const { value } = event.target;
        if ((name === 'min' || name === 'max') && !/[0-9]|\./.test(value)) return;

        disabledSave.setFalse();
        if (name === 'type' && value === 'CLAUSE') {
            setState({
                ...state,
                min: '',
                max: '',
                [name]: value,
            });
            setErrors({
                ...errors,
                min: '',
                max: '',
            });
        } else if (name === 'type' && value === 'VALUE') {
            setState({
                ...state,
                max: '',
                min: '',
                clause: '',
                [name]: value,
            });
            setErrors({
                ...errors,
                min: '',
                max: '',
                clause: '',
            });
        } else
            setState({
                ...state,
                [name]: value,
            });

        validate(value, name, length, false);
    };

    const submit = () => {
        const requiredFields = ['type', 'min'];

        if (state.type === 'VALUE') requiredFields.push('max');
        else if (state.type === 'CLAUSE') requiredFields.push('clause');
        const withoutErrors = checkRequiredFields({ fields: requiredFields, state, setErrors, errors: {} });

        if (withoutErrors && state.type === 'CLAUSE' && state.clause !== 'NONE')
            setErrors({
                ...errors,
                clause: t('ra:requiredFieldError'),
            });

        // to check if user has such category already
        // to check if user filled all required fields
        const isDisabledSave =
            checkIfCategoryExists() || !withoutErrors || (+state.max <= +state.min && state.type === 'VALUE');

        if (withoutErrors && +state.max <= +state.min && state.type === 'VALUE')
            setErrors({
                ...errors,
                min: t('admin:endValueShouldBeGreaterThanStart'),
            });
        disabledSave.setValue(!!isDisabledSave);
        if (!isDisabledSave) onClose({ ...state, index: currentCategory?.index ?? undefined });
    };

    return (
        <CustomDialog
            onClose={() => {
                onClose();
            }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
            className="custom-width-popup add-crf-modal max-w700"
        >
            <DialogTitle id="edit-category-title-id">
                {currentCategory ? t('cm:editCategory') : t('cm:addCategory')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-category-dialog-content">
                <div className="fields-wrapper">
                    <SelectWithCategories
                        label={t('type')}
                        dataArray={types}
                        onChange={onChange}
                        fieldSelect="type"
                        currentState={state.type}
                        searchable
                        filterable={false}
                        labelField="type"
                        showWarning={false}
                        customClassName="form-action-type"
                    />
                    {state.type === 'VALUE' ? (
                        <>
                            <TextField
                                label={t('cm:startValue')}
                                id="edit-range-startValue"
                                fullWidth
                                value={state.min}
                                onChange={onChange('min')}
                                variant="outlined"
                                color="secondary"
                                className="range-input"
                                type="number"
                                error={!!errors.min}
                                helperText={<CharactersRemaining id="min-text" error={errors.min} />}
                            />
                            <TextField
                                label={t('cm:endValue')}
                                id="edit-range-endValue"
                                fullWidth
                                value={state.max}
                                onChange={onChange('max')}
                                variant="outlined"
                                color="secondary"
                                className="range-input mr-0"
                                type="number"
                                error={!!errors.max}
                                helperText={<CharactersRemaining id="max-text" error={errors.max} />}
                            />
                        </>
                    ) : state.type === 'CLAUSE' ? (
                        <>
                            <SelectWithCategories
                                label={t('cm:clause')}
                                dataArray={Constants.clauses}
                                onChange={onChange}
                                fieldSelect="clause"
                                currentState={state.clause}
                                filterable={false}
                                labelField="clause"
                                showWarning={false}
                                customClassName="form-action-type"
                                error={errors.clause}
                            />
                            <TextField
                                label={t('value')}
                                id="edit-range-value"
                                fullWidth
                                className="range-input mr-0"
                                value={state.min}
                                onChange={onChange('min')}
                                variant="outlined"
                                color="secondary"
                                type="number"
                                error={!!errors.min}
                                helperText={<CharactersRemaining id="min-text" error={errors.min} />}
                            />
                        </>
                    ) : null}
                </div>
                <DialogActions className="add-crf-dialog-content-actions">
                    <Button id="save-category-btn" disabled={disabledSave.value} text="save" onClick={submit} />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default EditRange;
